/*
  What the browser tab says.

  Titles were being added page by page, which works and is what most of `src/dashboard` does —
  but it means a page only has one if somebody remembered, and nobody remembers on the thirtieth
  page. It had already drifted three ways: most said "Wabenzy Autos - X", one said just "Car
  Requests", and one said "Cars (New Home)".

  So the FLOOR comes from the route. `RouteTitle` sits in the dashboard layout, looks the current
  path up here, and every page gets a title whether or not its author thought about it — including
  pages that do not exist yet.

  A page that wants something more specific still says so, with `<PageTitle>` or a plain `Helmet`.
  react-helmet resolves to the LAST-mounted title, and a page mounts inside the layout, so a page's
  own always wins over the floor. That is the whole mechanism; there is nothing to co-ordinate.

  The words here are not the sidebar's. A tab is read out of context — half-hidden, next to eleven
  others — so it says "Duty requests" where the sidebar can afford to say "View".
*/
import React from 'react'
import PropTypes from 'prop-types'
import { Helmet } from 'react-helmet'
import { useLocation } from 'react-router-dom'

/** Prefixed once, here, so the three spellings this had grown cannot come back. */
export const BRAND = 'Wabenzy Autos'

/**
 * Longest match wins, so `/custom-duties/shipping-fees` beats `/custom-duties` without the order
 * of this object mattering. Declared shortest-first anyway, because it reads as the menu does.
 */
const ROUTES = {
  '/': 'Overview',

  '/cars': 'Cars',
  '/cars/add-new': 'Add a car',
  '/car-orders': 'Car orders',
  '/car-orders/add-new': 'New car order',
  '/car-requests': 'Car requests',
  '/car-tasks': 'Car preparation tasks',

  // Added with the vehicle-history work, not by it: the inventory assistant shipped without a
  // title, so its three pages read "Overview" in the browser tab, and `PageTitle.test.js` had
  // been failing on it.
  '/inventory-assistant': 'Inventory assistant',
  '/inventory-assistant/changes': 'Change log',
  '/inventory-assistant/people': 'Who can use the inventory assistant',
  '/inventory-assistant/usage': 'AI usage',

  '/custom-duties': 'Duty requests',
  '/custom-duties/customers': 'Duty customers',
  '/custom-duties/msrp-whitelist': 'MSRP whitelist',
  '/custom-duties/banking-rates': 'Banking rates',
  '/custom-duties/car-rates': 'Car rates',
  '/custom-duties/shipping-fees': 'Shipping fees',

  '/duty-assistant/conversations': 'Duty conversations',
  '/duty-assistant/dealers': 'Dealers',
  '/duty-assistant/drive-train-badges': 'Drive train badges',
  '/duty-assistant/payments': 'Duty payment settings',
  '/duty-assistant/pricing': 'Duty pricing',
  '/duty-assistant/promotions': 'Promotions',

  '/settings/assistant': 'Assistant settings',
  '/settings/knowledge-base': 'Knowledge base',
  '/settings/follow-ups': 'Follow-up messages',
  '/settings/broadcasts': 'Broadcasts',
  '/settings/payment-routes': 'Payment routes',

  '/insurance/policies': 'Insurance policies',
  '/insurance/settlement': 'Insurance settlement',
  '/insurance/setup': 'Insurance setup',

  '/tracking': 'Shipment tracking',
  '/tracking/shipments': 'Shipments',
  '/tracking/bills-of-lading': 'Bills of lading',
  '/tracking/conversations': 'Tracking conversations',
  '/tracking/lines': 'Shipping lines',
  '/tracking/pricing': 'Tracking pricing',
  '/tracking/account': 'ShipsGo account',
  '/vehicle-history': 'Vehicle history',
  '/growth': 'Growth',
  '/growth/opportunities': 'Opportunities',
  '/growth/places': 'Places in reach',
  '/growth/reviews': 'Weekly reviews',
  '/growth/runs': 'Growth runs & spend',
  '/growth/settings': 'Growth settings',
  '/vehicle-history/orders': 'Vehicle history orders',
  '/vehicle-history/conversations': 'History conversations',
  '/vehicle-history/products': 'Report packages',
  '/vehicle-history/supplier': 'GlobalVIN account',

  '/transactions': 'Transactions',
  '/customers': 'Customers',
  '/customers/add-new': 'New customer',
  '/users': 'Users',
  '/users/add-new': 'New user',
  '/users/my-account': 'My account',

  '/auth/login': 'Sign in'
}

/**
 * The title for a path, brand included.
 *
 * Longest prefix wins, so `/cars/some-slug` is "Cars" rather than falling through to "Overview" —
 * a detail page with no title of its own still says roughly where it is, which is the whole point
 * of having a floor. Exported and pure so it can be reasoned about without a browser.
 */
export function titleForPath(pathname) {
  const path = (pathname || '/').replace(/\/+$/, '') || '/'
  let best = ''
  Object.keys(ROUTES).forEach(route => {
    if (route === '/') return
    if ((path === route || path.startsWith(`${route}/`)) && route.length > best.length) {
      best = route
    }
  })
  const name = best ? ROUTES[best] : ROUTES['/']
  return `${BRAND} - ${name}`
}

/** The floor. One of these, in the layout — not per page. */
export function RouteTitle() {
  const location = useLocation()
  return (
    <Helmet>
      <title>{titleForPath(location.pathname)}</title>
    </Helmet>
  )
}

/**
 * A page saying its own, when the route cannot know it — a customer's name, an order's number.
 *
 * Mounts inside the layout, so it wins over `RouteTitle` with no co-ordination between them.
 */
export function PageTitle({ title }) {
  return (
    <Helmet>
      <title>{title ? `${BRAND} - ${title}` : BRAND}</title>
    </Helmet>
  )
}

PageTitle.propTypes = { title: PropTypes.string }

export default PageTitle
